import type { AppConfig } from '../types'
import type { Action, BookingState, Step } from '../booking/state'
import { flowSteps, stepFilled } from '../booking/state'

const CRUMB_LABELS: Record<Step, string> = {
  service: 'Service',
  subProfile: 'Visit type',
  patientType: 'Patient',
  region: 'Location',
  slots: 'Time',
  details: 'Details',
  confirmation: ''
}

/** A lightweight progress breadcrumb. Built from flowSteps() so conditional
 *  steps (sub-profile, region, slots) always match the real flow. */
export function Crumbs({
  config,
  state,
  dispatch
}: {
  config: AppConfig
  state: BookingState
  dispatch: React.Dispatch<Action>
}) {
  if (state.step === 'confirmation') return null

  const steps = flowSteps(config, state).map((key) => ({ key, label: CRUMB_LABELS[key] }))

  const currentIdx = steps.findIndex((s) => s.key === state.step)

  // A crumb is navigable (back or forward) when every step before it already
  // has its selection — i.e. jumping there can't land on a half-empty screen.
  const navigable = (idx: number): boolean =>
    idx !== currentIdx && steps.slice(0, idx).every((s) => stepFilled(state, s.key))

  return (
    <nav className="crumbs" aria-label="Progress">
      {steps.map((s, i) => (
        <span key={s.key} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {i > 0 && <span className="sep">›</span>}
          {navigable(i) ? (
            <button
              type="button"
              className="crumb crumb-link"
              style={i > currentIdx ? { opacity: 0.55 } : undefined}
              onClick={() => dispatch({ type: 'EDIT_STEP', step: s.key })}
            >
              {s.label}
            </button>
          ) : (
            <span
              className={`crumb${i === currentIdx ? ' active' : ''}`}
              aria-current={i === currentIdx ? 'step' : undefined}
              style={i > currentIdx ? { opacity: 0.55 } : undefined}
            >
              {s.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  )
}
